import { useIntl } from 'react-intl'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import noop from 'lib/function/noop'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

const ButtonWrapper = styled.div`
  position: absolute;
  bottom: calc(100% + ${rem(16)});
  inset-inline-start: 50%;
  transform: translateX(-50%);
  z-index: 10;

  opacity: 0;
  pointer-events: none;

  transition: opacity 0.2s ease;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }

  [dir='rtl'] & {
    transform: translateX(50%);
  }
`

const StyledIconButton = styled(IconButton)`
  box-shadow: ${props => props.theme.shadow.bottom.small};

  /*
   * Rebrand: opaque white surface with a black icon, so the control stays
   * legible over whatever chat content it floats above. Mirrors the overlay
   * close-button treatment in ModalPopupsGate rather than switching kind,
   * which would pull the experiment lookup into this presentational leaf.
   */
  body.${REBRAND_PALETTE_CLASS} & {
    --button-internal-default-color: ${props => props.theme.palette.neutral.surface.normalContrast};
    --button-internal-default-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    --button-internal-default-border-color: var(--button-transparent);
    --button-internal-focus-color: ${props => props.theme.palette.neutral.surface.normalContrast};
    --button-internal-focus-background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    --button-internal-focus-border-color: var(--button-transparent);
    --button-internal-hover-color: ${props => props.theme.palette.neutral.surface.normalContrast};
    --button-internal-hover-background-color: color-mix(in srgb, ${props => props.theme.palette.neutral.surface.normalBackground}, ${props => props.theme.palette.neutral.control.color.black} ${props => props.theme.palette.neutral.control.opacity[8]});
    --button-internal-hover-border-color: var(--button-transparent);
    --button-internal-active-color: ${props => props.theme.palette.neutral.surface.normalContrast};
    --button-internal-active-background-color: color-mix(in srgb, ${props => props.theme.palette.neutral.surface.normalBackground}, ${props => props.theme.palette.neutral.control.color.black} ${props => props.theme.palette.neutral.control.opacity[16]});
    --button-internal-active-border-color: var(--button-transparent);
  }
`

interface Props {
  visible: boolean
  onClick?: () => void
}

export default function AssistantScrollToBottomButton({ visible, onClick = noop }: Props) {
  const intl = useIntl()

  const scrollToBottomAriaLabel = intl.formatMessage({
    id: 'JtEwYrQXHepi-d_CAscE',
    defaultMessage: 'Scroll to bottom',
    description: 'Accessible label for the scroll to bottom button in the chat panel',
  })

  return (
    <ButtonWrapper className={clsx({ visible })}>
      <StyledIconButton
        kind="secondary"
        variant="default"
        shape="circle"
        size="large"
        onClick={onClick}
        aria-label={scrollToBottomAriaLabel}
        aria-hidden={!visible}
        tabIndex={visible ? undefined : -1}
        type="button"
      >
        <LineArrowDownIcon />
      </StyledIconButton>
    </ButtonWrapper>
  )
}
